// AGC: Purchase Order Service — client-side wrapper around /api/v1/purchase-orders.
// AGC: All persistence runs server-side via Admin SDK (bypasses Firestore rules, sanitises undefined).
// AGC: Signatures preserve (userId, farmId) args for call-site compatibility; backend derives
// AGC: the real userId/farmId from the verified JWT and ignores whatever the client passed.

import { api } from './api'
import type {
  PurchaseOrder,
  PurchaseOrderStatus,
  CreatePurchaseOrderData,
  ConfirmReceiptData,
  PendingOrderSummary,
} from '../types/inventory'

const BASE = '/api/v1/purchase-orders'

interface OrderResponse { success: true; data: PurchaseOrder }
interface OrderListResponse { success: true; data: PurchaseOrder[] }
interface ReceiveResponse {
  success: true
  data: { inventoryUpdated: number; expenseCreated: boolean }
}

export async function createPurchaseOrder(
  _userId: string,
  _farmId: string,
  data: CreatePurchaseOrderData,
): Promise<PurchaseOrder> {
  const res = await api.post<OrderResponse>(BASE, data)
  return res.data
}

export async function getPurchaseOrder(
  _userId: string,
  _farmId: string,
  orderId: string,
): Promise<PurchaseOrder | null> {
  try {
    const res = await api.get<OrderResponse>(`${BASE}/${orderId}`)
    return res.data
  } catch (error) {
    if (error instanceof Error && 'status' in error && (error as { status: number }).status === 404) {
      return null
    }
    throw error
  }
}

export async function getPurchaseOrders(
  _userId: string,
  _farmId: string,
  status?: PurchaseOrderStatus,
): Promise<PurchaseOrder[]> {
  const path = status ? `${BASE}?status=${encodeURIComponent(status)}` : BASE
  const res = await api.get<OrderListResponse>(path)
  return res.data
}

export async function updatePurchaseOrder(
  _userId: string,
  _farmId: string,
  orderId: string,
  data: Partial<Omit<PurchaseOrder, 'id' | 'createdAt' | 'createdBy'>>,
): Promise<void> {
  await api.patch<OrderResponse>(`${BASE}/${orderId}`, data)
}

export async function sendPurchaseOrder(
  _userId: string,
  _farmId: string,
  orderId: string,
  sharedVia: 'whatsapp' | 'email' | 'other',
): Promise<void> {
  await api.post<OrderResponse>(`${BASE}/${orderId}/send`, { sharedVia })
}

export async function cancelPurchaseOrder(
  _userId: string,
  _farmId: string,
  orderId: string,
  reason?: string,
): Promise<void> {
  await api.post<OrderResponse>(`${BASE}/${orderId}/cancel`, { reason })
}

export async function deletePurchaseOrder(
  _userId: string,
  _farmId: string,
  orderId: string,
): Promise<void> {
  await api.delete<{ success: boolean }>(`${BASE}/${orderId}`)
}

export async function confirmOrderReceipt(
  _userId: string,
  _farmId: string,
  orderId: string,
  data: ConfirmReceiptData,
): Promise<{ inventoryUpdated: number; expenseCreated: boolean }> {
  const res = await api.post<ReceiveResponse>(`${BASE}/${orderId}/receive`, data)
  return res.data
}

// AGC: Computed locally from getPurchaseOrders — no dedicated endpoint needed.
export async function getOrdersForInput(
  userId: string,
  farmId: string,
  inputId: string,
): Promise<PendingOrderSummary | null> {
  const orders = await getPurchaseOrders(userId, farmId)
  const pending = orders.filter(o => o.status !== 'received' && o.status !== 'cancelled')
  const withInput = pending.filter(order =>
    order.items.some(item => item.inputId === inputId && item.status !== 'received'),
  )
  if (withInput.length === 0) return null

  let totalOrdered = 0
  const details = withInput.map(order => {
    const item = order.items.find(i => i.inputId === inputId)!
    const pendingQty = item.requestedQuantity - (item.receivedQuantity || 0)
    totalOrdered += pendingQty
    return {
      orderId: order.id,
      orderNumber: order.orderNumber,
      quantity: pendingQty,
      status: order.status,
      expectedDeliveryDate: order.expectedDeliveryDate,
    }
  })

  return { inputId, totalOrdered, orders: details }
}

// AGC: Computed locally from getPurchaseOrders.
export async function getPendingOrdersSummary(
  userId: string,
  farmId: string,
): Promise<Map<string, PendingOrderSummary>> {
  const orders = await getPurchaseOrders(userId, farmId)
  const pending = orders.filter(o => o.status !== 'received' && o.status !== 'cancelled')
  const map = new Map<string, PendingOrderSummary>()

  pending.forEach(order => {
    order.items.forEach(item => {
      if (item.status === 'received') return
      const pendingQty = item.requestedQuantity - (item.receivedQuantity || 0)
      if (pendingQty <= 0) return

      const entry = map.get(item.inputId)
      const orderRef = {
        orderId: order.id,
        orderNumber: order.orderNumber,
        quantity: pendingQty,
        status: order.status,
        expectedDeliveryDate: order.expectedDeliveryDate,
      }
      if (entry) {
        entry.totalOrdered += pendingQty
        entry.orders.push(orderRef)
      } else {
        map.set(item.inputId, {
          inputId: item.inputId,
          totalOrdered: pendingQty,
          orders: [orderRef],
        })
      }
    })
  })

  return map
}

export function calculateItemCost(
  requestedQuantity: number,
  costPerUnit?: number,
): { estimatedCostPerUnit?: number; estimatedTotalCost?: number } {
  if (!costPerUnit || costPerUnit <= 0) return {}
  return {
    estimatedCostPerUnit: costPerUnit,
    estimatedTotalCost: requestedQuantity * costPerUnit,
  }
}

// =============================================================================
// AGC: Favorite Suppliers
// =============================================================================

export interface FavoriteSupplier {
  id: string
  name: string
  contact?: string | null
  phone?: string | null
  email?: string | null
  address?: string | null
}

interface FavSupplierListResponse { success: true; data: FavoriteSupplier[] }
interface FavSupplierResponse { success: true; data: FavoriteSupplier }

export async function getFavoriteSuppliers(): Promise<FavoriteSupplier[]> {
  const res = await api.get<FavSupplierListResponse>(`${BASE}/favorite-suppliers`)
  return res.data
}

export async function addFavoriteSupplier(
  supplier: Omit<FavoriteSupplier, 'id'>,
): Promise<FavoriteSupplier> {
  const res = await api.post<FavSupplierResponse>(`${BASE}/favorite-suppliers`, supplier)
  return res.data
}

export async function removeFavoriteSupplier(supplierId: string): Promise<void> {
  await api.delete<{ success: boolean }>(`${BASE}/favorite-suppliers/${supplierId}`)
}

export default {
  createPurchaseOrder,
  getPurchaseOrder,
  getPurchaseOrders,
  getOrdersForInput,
  updatePurchaseOrder,
  sendPurchaseOrder,
  confirmOrderReceipt,
  cancelPurchaseOrder,
  deletePurchaseOrder,
  getPendingOrdersSummary,
  calculateItemCost,
  getFavoriteSuppliers,
  addFavoriteSupplier,
  removeFavoriteSupplier,
}
